import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Film, ArrowRight, Sparkles } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import BloomingFlowerOverlay from '../BloomingFlowerOverlay';
import { APPS } from '../../config/suiteRegistry';
import { playBloomChime } from '../../lib/bloomChime';

const TD_URL  = 'https://www.td-trustdesign.com';
const TVD_URL = 'https://trusttvd.com';

/**
 * EcosystemBridge — "Next Steps in your Creator Journey" (link-only hub)
 * ──────────────────────────────────────────────────────────────────────────
 * Per the May 8 strategic pivot, LT/TD/TVD are independent platforms.
 * This bridge is a premium navigational hand-off — no SSO, no token, no
 * shared session. Just a cinematic 3-second bloom transition to the
 * destination's marketing/login URL.
 */
const EcosystemBridge = ({ language: _legacyLang }) => {
  const { language, localize } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => localize({ ar, en });
  const [bloomTarget, setBloomTarget] = useState(null);

  const openTarget = (key, url) => {
    // Fire chime within user gesture for iOS audio unlock
    playBloomChime();
    const app = APPS[key] || { key, name: key.toUpperCase(), brand: '#6366F1' };
    setBloomTarget({ app, url });
  };

  const handleBloomComplete = () => {
    if (!bloomTarget) return;
    const w = window.open(bloomTarget.url, '_blank', 'noopener,noreferrer');
    if (!w) window.location.href = bloomTarget.url;
    setTimeout(() => setBloomTarget(null), 200);
  };

  return (
    <section
      dir={isAr ? 'rtl' : 'ltr'}
      className="relative py-12 sm:py-16 px-4 sm:px-6"
      data-testid="ecosystem-bridge"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#0f172a] via-[#1e1b4b] to-[#312e81] text-white p-6 sm:p-10 shadow-[0_20px_60px_rgba(67,56,202,0.4)]"
        >
          {/* Decorative blurred orbs */}
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-emerald-400/15 blur-3xl pointer-events-none" aria-hidden />
          <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full bg-fuchsia-500/15 blur-3xl pointer-events-none" aria-hidden />

          {/* Kicker */}
          <div className="relative inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-300/90 bg-emerald-500/10 ring-1 ring-emerald-400/20 px-2.5 py-1 rounded-full mb-4">
            <Sparkles className="w-3 h-3" />
            {t('منظومة Trust الثلاثية', 'Trust 3-Tier Ecosystem')}
          </div>

          <h2 className="relative text-2xl sm:text-3xl font-extrabold leading-tight mb-2">
            {t('الخطوات التالية في رحلتك الإبداعية', 'Next Steps in your Creator Journey')}
          </h2>
          <p className="relative text-sm sm:text-base text-indigo-100/80 max-w-2xl mb-7 leading-relaxed">
            {t(
              'بعد كتابة دروسك ومذكراتك على Learn With Trust، انقُلها مباشرة إلى Trust Design لتصميم كتابك الاحترافي على Amazon KDP، أو إلى Trust Visual Director لتحويلها إلى فيلم سينمائي بالذكاء الاصطناعي.',
              'After writing your lessons and notes on Learn With Trust, jump straight to Trust Design to publish your professional book on Amazon KDP, or to Trust Visual Director to convert them into an AI-cinematic film.'
            )}
          </p>

          {/* Two CTAs */}
          <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {/* Publish as Book → TD (direct link with bloom transition) */}
            <button
              type="button"
              onClick={() => openTarget('td', TD_URL)}
              className="group relative overflow-hidden rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] ring-1 ring-white/15 p-5 text-left transition-all hover:scale-[1.015] active:scale-[0.99]"
              data-testid="bridge-publish-book-btn"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-[0_6px_20px_rgba(16,185,129,0.4)]">
                  <BookOpen className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-emerald-300 bg-emerald-500/10 ring-1 ring-emerald-400/30 px-1.5 py-0.5 rounded">
                      TD
                    </span>
                  </div>
                  <h3 className="text-lg font-extrabold mb-0.5">
                    {t('انشر ككتاب', 'Publish as Book')}
                  </h3>
                  <p className="text-xs text-indigo-100/70 leading-snug">
                    {t('Trust Design — محرر كتب احترافي بمستوى Canva مع PDF بدقة 300 DPI', 'Trust Design — Canva-sleek editor with KDP-safe 300 DPI PDFs')}
                  </p>
                </div>
                <ArrowRight className={`w-4 h-4 text-white/40 group-hover:text-emerald-300 transition-colors shrink-0 ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {/* Convert to Movie → TVD (direct link with bloom transition) */}
            <button
              type="button"
              onClick={() => openTarget('tvd', TVD_URL)}
              className="group relative overflow-hidden rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] ring-1 ring-white/15 p-5 text-left transition-all hover:scale-[1.015] active:scale-[0.99]"
              data-testid="bridge-convert-movie-btn"
            >
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 flex items-center justify-center shadow-[0_6px_20px_rgba(217,70,239,0.4)]">
                  <Film className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[9px] font-bold tracking-[0.15em] uppercase text-fuchsia-200 bg-fuchsia-500/10 ring-1 ring-fuchsia-400/30 px-1.5 py-0.5 rounded">
                      TVD
                    </span>
                  </div>
                  <h3 className="text-lg font-extrabold mb-0.5">
                    {t('حوّل إلى فيلم', 'Convert to Movie')}
                  </h3>
                  <p className="text-xs text-indigo-100/70 leading-snug">
                    {t('Trust Visual Director — بوابة سينمائية تحوّل المخطوطات إلى أفلام بالـ AI', 'Trust Visual Director — AI cinematic portal turning manuscripts into films')}
                  </p>
                </div>
                <ArrowRight className={`w-4 h-4 text-white/40 group-hover:text-fuchsia-300 transition-colors shrink-0 ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>

          {/* AI transparency note — softened tone, removed "AI-generated" verbiage (Feb 2026) */}
          <p className="relative mt-6 text-center text-[11px] text-emerald-300/85 flex items-center justify-center gap-1.5">
            <span aria-hidden>✨</span>
            {t(
              'معاينات تفاعلية لتجربة كاملة عبر منظومة Trust',
              'Interactive previews of the full Trust ecosystem experience'
            )}
          </p>
        </motion.div>
      </div>

      {/* 3-second Blooming-Flower transition before opening the destination */}
      <AnimatePresence>
        {bloomTarget && (
          <BloomingFlowerOverlay
            target={bloomTarget.app}
            onComplete={handleBloomComplete}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default EcosystemBridge;
